/**
 * Platform-notification state — backs the shell platform-notification toaster.
 *
 * Fed by the SignalR `portalnotificationmethod` hub (see
 * `platform-notifications-bridge.ts`). Unlike the status-page incident banner
 * (which shows a single latest incident), platform notifications are a QUEUE:
 * each pushed notification is its own dismissible toast, stacked bottom-left so
 * they never overlap. Toasts persist until the user closes them (no auto-dismiss).
 *
 * Payload shape (see PRTL-2900 sample):
 *   {
 *     notificationId, type, title, message, source,
 *     action?: { label, url }, metadata?, timestampUtc
 *   }
 */

import { create } from 'zustand';

/** Platform-notification categories the toaster styles + announces differently. */
export type PlatformNotificationType = 'action' | 'info' | 'warning' | 'error';

const PLATFORM_NOTIFICATION_TYPES: readonly PlatformNotificationType[] = [
  'action',
  'info',
  'warning',
  'error',
];

/** Optional call-to-action rendered as a link/button on the toast. */
export interface PlatformNotificationAction {
  label: string;
  url: string;
}

/** Normalized platform notification the toaster renders. */
export interface PlatformNotificationView {
  id: string;
  type: PlatformNotificationType;
  title: string;
  message: string;
  /** Originating app name, e.g. "MAD". */
  source?: string;
  action?: PlatformNotificationAction;
  metadata?: Record<string, unknown>;
  timestampUtc?: string;
}

interface RawPlatformNotificationAction {
  label?: string;
  text?: string;
  title?: string;
  url?: string;
  href?: string;
  link?: string;
}

interface RawPlatformNotification {
  notificationId?: string;
  notification_id?: string;
  id?: string;
  type?: string;
  title?: string;
  message?: string;
  source?: string;
  // The feed may nest the CTA as an object, send it as a bare URL string, or
  // split it across top-level `actionUrl`/`actionLabel` fields.
  action?: RawPlatformNotificationAction | string | null;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  timestampUtc?: string;
  timestamp_utc?: string;
  timestamp?: string;
}

let fallbackId = 0;

/** Stable id for a notification that arrives without one. */
function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `platform-notification-${Date.now()}-${fallbackId++}`;
}

/** Coerce an arbitrary `type` to a known category, defaulting to `info`. */
function normalizeType(value: unknown): PlatformNotificationType {
  const normalized = String(value ?? '').toLowerCase();
  return (PLATFORM_NOTIFICATION_TYPES as readonly string[]).includes(normalized)
    ? (normalized as PlatformNotificationType)
    : 'info';
}

/**
 * Extract the call-to-action from the assorted shapes the feed may use: a nested
 * object (`url` / `href` / `link` + `label` / `text` / `title`), a bare URL
 * string, or top-level `actionUrl` / `actionLabel` fields. Returns `undefined`
 * when no usable URL is present (so no button is rendered).
 */
function normalizeAction(item: RawPlatformNotification): PlatformNotificationAction | undefined {
  const raw = item.action;
  if (typeof raw === 'string') {
    if (!raw || !isHttpUrl(raw)) return undefined;
    return { label: item.actionLabel || 'Open', url: raw };
  }
  const url = raw?.url || raw?.href || raw?.link || item.actionUrl;
  if (!url || !isHttpUrl(url)) return undefined;
  const label = raw?.label || raw?.text || raw?.title || item.actionLabel || 'Open';
  return { label, url };
}

/** Only allow http(s) URLs — reject javascript:, data:, etc. */
function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Reconcile a raw payload (snake_case / camelCase tolerant) into a
 * `PlatformNotificationView`. Returns `undefined` when there is nothing worth
 * showing (no object, or neither a title nor a message).
 */
export function normalizePlatformNotification(raw: unknown): PlatformNotificationView | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const item = raw as RawPlatformNotification;

  const title = typeof item.title === 'string' ? item.title : '';
  const message = typeof item.message === 'string' ? item.message : '';
  if (!title && !message) return undefined;

  return {
    id: item.notificationId ?? item.notification_id ?? item.id ?? generateId(),
    type: normalizeType(item.type),
    title,
    message,
    source: item.source,
    action: normalizeAction(item),
    metadata: item.metadata,
    timestampUtc: item.timestampUtc ?? item.timestamp_utc ?? item.timestamp,
  };
}

/**
 * Reconcile a payload that may be a single notification, a bare array, or a
 * `{ notifications: [] }` envelope into `PlatformNotificationView[]`.
 */
export function normalizePlatformNotifications(payload: unknown): PlatformNotificationView[] {
  const container = payload as { notifications?: unknown } | unknown[] | null;
  const items = Array.isArray(container)
    ? container
    : container && typeof container === 'object' && Array.isArray(container.notifications)
      ? container.notifications
      : [payload];

  return items
    .map((item) => normalizePlatformNotification(item))
    .filter((item): item is PlatformNotificationView => Boolean(item));
}

interface PlatformNotificationState {
  items: PlatformNotificationView[];
  /** Add a notification; a matching id replaces the existing entry (dedupe). */
  push: (notification: PlatformNotificationView) => void;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const usePlatformNotificationStore = create<PlatformNotificationState>((set) => ({
  items: [],
  push: (notification) =>
    set((s) => ({
      // Re-pushing the same id updates in place instead of stacking a duplicate.
      items: [...s.items.filter((item) => item.id !== notification.id), notification],
    })),
  dismiss: (id) => set((s) => ({ items: s.items.filter((item) => item.id !== id) })),
  clear: () => set({ items: [] }),
}));

/** Push a single raw platform-notification payload (normalized). No-op when unusable. */
export function pushPlatformNotification(payload: unknown): void {
  const notification = normalizePlatformNotification(payload);
  if (notification) usePlatformNotificationStore.getState().push(notification);
}

/** Push one or many raw platform notifications from a hub payload (normalized). */
export function pushPlatformNotifications(payload: unknown): void {
  const store = usePlatformNotificationStore.getState();
  for (const notification of normalizePlatformNotifications(payload)) {
    store.push(notification);
  }
}

export function dismissPlatformNotification(id: string): void {
  usePlatformNotificationStore.getState().dismiss(id);
}
